import { Link } from 'react-router-dom';
import { Trophy, BadgeCheck, Clock, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FAQSection, FinalCTA } from './DubaiCostOfLivingPage';

const PATHWAYS = [
  {
    title: 'Golden Visa — 10 Years', icon: Trophy, badge: 'Outstanding Graduate',
    chips: ['10-year residency', 'Self-sponsored', 'Family included', 'Renewable'],
    eligibility: 'GPA 3.5+ (or first-class honours) from a UAE-accredited or globally top-100 university. Issued under Federal Decree-Law No. 29 of 2021.',
    docs: ['University attestation letter', 'Academic transcripts (notarised)', 'Passport copy + Emirates ID', 'Personal bank balance proof (no fixed minimum)', 'Medical fitness + health insurance'],
  },
  {
    title: 'Green Visa — 5 Years', icon: BadgeCheck, badge: 'Skilled Worker',
    chips: ['5-year residency', 'Self-sponsored', 'Family included', 'No employer needed'],
    eligibility: 'Bachelor\u2019s degree + minimum monthly salary of AED 15,000 in an MoHRE-classified Level 1/2/3 skilled job. Ideal for graduates 2 years into a Dubai career.',
    docs: ['Bachelor\u2019s attestation', 'Active UAE employment contract', 'Salary slips (last 6 months)', 'Health insurance certificate'],
  },
  {
    title: 'Job Seeker Visa — 60/90/120 Days', icon: Clock, badge: 'Post-Graduation Entry',
    chips: ['Sponsor-free', 'In-country conversion', 'Top 500 grads', 'Multi-entry'],
    eligibility: 'Available to graduates of top 500 global universities or UAE-accredited universities. Lets you remain in the UAE legally while finalising an offer letter.',
    docs: ['Degree certificate', 'CV', 'Health insurance for the chosen duration', 'Return ticket proof'],
  },
];

const FAQS = [
  { q: 'What GPA do I need for the UAE Golden Visa as an outstanding graduate?', a: 'A minimum GPA of 3.5/4.0 (or first-class honours) from a UAE-accredited or top-100 global university qualifies you for the outstanding-graduate Golden Visa. The 10-year residency is renewable and includes spouse and children.' },
  { q: 'Is the Dubai Golden Visa free?', a: 'No — government fees are roughly AED 2,800 – 3,800 depending on emirate and processing speed, plus medical and Emirates ID issuance fees. ZAFCO bundles the application as part of post-study services.' },
  { q: 'Can I apply for the UAE Job Seeker Visa from India?', a: 'Yes. The Job Seeker Visa is issued from outside the UAE through the ICP smart-services portal. Choose 60 / 90 / 120-day durations based on how long you need to interview and accept an offer. It cannot be converted directly to residency without an employment contract.' },
  { q: 'Which is better — Golden Visa or Green Visa?', a: 'Golden Visa gives 10 years and is the gold standard if you graduate with a high GPA. Green Visa is 5 years and is easier to qualify for once you have a Dubai job paying AED 15,000+/month. Most Indian graduates start on Green, then upgrade to Golden after 3–4 years.' },
];

export default function DubaiGoldenVisaPage() {
  usePageMeta(
    'Dubai Golden Visa & Post-Study Pathways for Indian Students (2026)',
    '2026 guide to the UAE Golden Visa, Green Visa and Job Seeker Visa for Indian students who graduate in Dubai — eligibility GPA, document checklists, and step-by-step conversion from student to long-term resident.'
  );
  useFAQSchema(FAQS);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-golden-visa-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Golden Visa & Post-Study Pathways', url: 'https://zafco.in/study-abroad/dubai/golden-visa-post-study-pathways/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Golden Visa &amp; Post-Study Pathways</h1>
          <p className="mt-4 text-slate-300 max-w-3xl leading-relaxed">The UAE has the most graduate-friendly residency framework in the world. Here are the three pathways that take you from a 1-year student visa to a <strong className="text-white">10-year self-sponsored Golden Visa</strong> — without ever leaving Dubai.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-5">
            {PATHWAYS.map((p) => (
              <article key={p.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] hover:shadow-md transition-all" data-testid={`pathway-${p.title.split(' ')[0].toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <p.icon className="w-8 h-8 text-[#D4AF37]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#0F1B2C] text-white rounded-full px-2.5 py-1">{p.badge}</span>
                </div>
                <h3 className="text-lg font-bold font-syne text-[#0F1B2C] mb-3">{p.title}</h3>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.chips.map((c) => <span key={c} className="text-[10px] font-semibold bg-amber-50 text-amber-800 rounded-full px-2 py-0.5 border border-amber-200">{c}</span>)}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{p.eligibility}</p>
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Documents</h4>
                <ul className="space-y-1.5">
                  {p.docs.map((d) => (
                    <li key={d} className="flex gap-2 text-xs text-slate-700">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" /> {d}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-9 p-5 bg-amber-50 border-l-4 border-[#D4AF37] rounded-r-2xl flex gap-3">
            <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-800 leading-relaxed"><strong>Strategy tip:</strong> Aim for a GPA 3.5+ in any KHDA-licensed Dubai degree, line up a DIFC / Internet City offer at AED 15,000+/month, and you can be on a Golden Visa within 6 months of graduation.</p>
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <FinalCTA source="dubai-golden-visa-pathways" />
    </div>
  );
}
